import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CSVOutputConst } from "../../../constants/CSVOutputConst";
import { BiddingItemStatusConst, ListingCategoryConst, type PrefectureType } from '../../../constants';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { useMasters } from '../../../contexts/MastersContext';
import { formatFromDateInput, formatToDateInput } from '../../../utils/date';
import { useGetBiddingDateForResultQuery } from '../../../hooks/useCalendarQuery';
import { useBiddingResultGetListQuery, useCsvExportOnly } from '../../../hooks/useBiddingResultQuery'; 
import { BiddingDisplayConditionConst } from '../../../constants/BiddingDisplayConditionConst';
import { useAppDialog } from '../../../hooks/useAppDialog';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

// 出品区分ドロップダウン用の選択肢
const PrefectureTypeOptions = [
  { value: 1, label: '通常入札 - 県内' },
  { value: 2, label: '通常入札 - 県外' },
];

//---------------------------------------------------------
// CSV再出力機能
//---------------------------------------------------------
export const CSVOutputPage = () => {

  //----------------
  // Context
  //----------------
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading } = useMasters();

  // 共通ダイアログ用フック
  const { showConfirm, OverlayElement } = useAppDialog();

  //----------------
  // State
  //----------------
  const [isInitialized, setIsInitialized] = useState(false);
  const [localParams, setLocalParams] = useState({
    targetDate: itemFilters.biddingDate ? formatToDateInput(itemFilters.biddingDate) : '',
    prefectureType: itemFilters.prefectureType || 1, 
  });
  const [exportTarget, setExportTarget] = useState<number | null>(null);

  const pageTitle = "連携用CSV再出力";

  //----------------
  // Data Fetching & API
  //----------------
  // APIへ渡す検索条件の組み立て
  const apiFilters = {
    ...itemFilters,
    listingCategory: ListingCategoryConst.BIDDING,
    status: BiddingItemStatusConst.CONFIRMED,
    prefectureType: Number(localParams.prefectureType),
    biddingDate: localParams.targetDate ? formatFromDateInput(localParams.targetDate) : itemFilters.biddingDate
  };

  // 落札結果日（初期表示用）の取得
  const { data: resultDate, isLoading: isResultLoading } = useGetBiddingDateForResultQuery(currentUser?.loginId);
  
  // 一覧データ（出力対象データ）の取得
  const { data: responseData, isLoading: isItemsLoading } = useBiddingResultGetListQuery(
    apiFilters, 
    currentUser?.loginId ?? "",
    { enabled: !!apiFilters.biddingDate } // 日付がセットされるまでAPI通信を待機
  );

  // CSV出力用カスタムフック
  const { handleExportCSV, isExporting } = useCsvExportOnly();

  //----------------
  // Memo / Variables
  //----------------
  const items = responseData?.items || [];
  const hasItems = items.length > 0;
  const isConfirmed = !!responseData?.isConfirm;
  const isSuccess = !!responseData?.isSuccess;

  /**
   * WebView2環境（デスクトップアプリのランタイム）かどうかを判定
   */
  const isWebView2 = !!(window.chrome && window.chrome.webview);

  // ボタン非活性条件: 「出力中」「専用アプリ外」「開催日ではない」「データなし」「未確定」のいずれかに該当する場合
  const isDisabled = isExporting || !isWebView2 || !isSuccess || !hasItems || !isConfirmed; 
  
  // 画面ローディング状態
  const isPageLoading = isMastersLoading || isResultLoading || isItemsLoading || !isInitialized;

  // 表示用テキストの出し分け
  const labelClasses = "block text-sm font-bold text-slate-700 mb-1";
  //const title = csvoutputType === CSVOutputConst.CORE_SYSTEM ? "基幹システム連携用CSV再出力" : "サイネージ連携用CSV再出力";

  // 状態に応じた警告メッセージの判定ロジック
  let warningMessage = "";

  if (!isWebView2) {
    warningMessage = "※専用アプリからのみ出力可能です";
  } else if (responseData) {
    if (!isSuccess) {
      warningMessage = "※指定された日付・出品区分では入札が開催されません。";
    } else if (!hasItems) {
      warningMessage = "※該当する取引情報がありません。";
    } else if (!isConfirmed) {
      warningMessage = "※指定された日付・出品区分では落札確定がされていません。";
    }
  }

  //----------------
  // Helpers
  //----------------
  /**
   * メッセージ表示用に日付を「◯年◯月◯日」形式に変換する
   */
  const formatJapaneseDate = (dateString: string): string => {
    if (!dateString) return "";
    const d = dayjs(dateString);
    return `${d.year()}年${d.month() + 1}月${d.date()}日`;
  };

  //----------------
  // Effects (初期値設定)
  //----------------
  useEffect(() => {
    // 読み込み中、未ログイン、または既に初期化済みの場合はスキップ
    if (isResultLoading || !currentUser || isInitialized) return;

    // APIから取得した日付があればそれを使用、なければ今日の日付を使用
    const rawDate = resultDate ? String(resultDate) : dayjs().tz().format("YYYYMMDD");
    const formatted = dayjs(rawDate.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD");
    
    // ローカルStateの更新
    setLocalParams(prev => ({ ...prev, targetDate: formatted }));
    
    // URLパラメータ(Context)の更新
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: ListingCategoryConst.BIDDING,
      status: BiddingItemStatusConst.CONFIRMED,
      biddingDisplayCondition: BiddingDisplayConditionConst.BIDDING,
      biddingDate: formatFromDateInput(formatted),
      prefectureType: itemFilters.prefectureType || 1
    });

    setIsInitialized(true); 
  }, [resultDate, isResultLoading, currentUser, isInitialized, itemFilters, updateFiltersAndNavigate]);

  // 出力が完了したらターゲットをクリアする
  useEffect(() => {
    if (!isExporting) {
      setExportTarget(null);
    }
  }, [isExporting]);

  //----------------
  // Event Handlers
  //----------------
  // 入力フォーム変更ハンドラ
  const handleChange = (field: string, value: string | number) => {
    // ローカルStateを更新
    const newParams = { ...localParams, [field]: value };
    setLocalParams(newParams);

    // URLパラメータ(Context)を更新して再フェッチをトリガー
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: ListingCategoryConst.BIDDING,
      status: BiddingItemStatusConst.CONFIRMED,
      biddingDisplayCondition: BiddingDisplayConditionConst.BIDDING,
      biddingDate: field === 'targetDate' ? formatFromDateInput(value as string) : formatFromDateInput(localParams.targetDate),
      prefectureType: field === 'prefectureType' ? Number(value) : Number(localParams.prefectureType),
    });
  };

  // 再出力実行ハンドラ
  // 基幹システム連携用
  const handleCoreSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled) return;

    // 共通ダイアログを表示
    showConfirm({
      title: pageTitle,
      message: `${formatJapaneseDate(localParams.targetDate)}の基幹システム連携用CSVを再出力してよろしいですか？`,
      onConfirm: () => {
        setExportTarget(CSVOutputConst.CORE_SYSTEM);

        // 並び替え処理
        // const sortedItems = [...items].sort((a, b) => {
        //      const numA = Number(a.receptionNumber) || 0; 
        //      const numB = Number(b.receptionNumber) || 0;
        //      return numA - numB; // 昇順に並び替え
        //    });

        handleExportCSV(items, Number(localParams.prefectureType) as PrefectureType, CSVOutputConst.CORE_SYSTEM);
      }
    });
  };

  // サイネージ連携用
  const handleSinageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled) return;

    // 共通ダイアログを表示
    showConfirm({
      title: pageTitle,
      message: `${formatJapaneseDate(localParams.targetDate)}のサイネージ連携用CSVを再出力してよろしいですか？`,
      onConfirm: () => {
        setExportTarget(CSVOutputConst.SINAGE);

        // 並び替え処理
        // const sortedItems = [...items].sort((a, b) => {
        //      const numA = Number(a.receptionNumber) || 0; 
        //      const numB = Number(b.receptionNumber) || 0;
        //      return numA - numB; // 昇順に並び替え
        //    });

        handleExportCSV(items, Number(localParams.prefectureType) as PrefectureType, CSVOutputConst.SINAGE);
      }
    });
  };

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  //----------------
  // Render
  //----------------
  if (isPageLoading) {
    return <div className="bg-white min-h-screen flex items-center justify-center font-bold text-slate-500">読み込み中...</div>;
  }

  return (
    <div className="bg-white min-h-screen font-sans pb-32 relative">
      
      {/* 共通背景レイヤー */}
      {OverlayElement}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ヘッダーセクション */}
        <header className="mb-8 flex justify-between items-center">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">{pageTitle}</h1>
        </header>

        <main>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex flex-wrap items-end gap-6 w-full">
              
              {/* 入札日入力セクション */}
              <div className="w-full sm:w-auto">
                <label className={labelClasses}>入札日</label>
                <input
                  type="date"
                  className="w-full sm:w-48 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500"
                  value={localParams.targetDate}
                  onChange={(e) => handleChange('targetDate', e.target.value)}
                />
              </div>

              {/* 出品区分セクション */}
              <div className="w-full sm:w-auto">
                <label className={labelClasses}>出品区分（県内/県外）</label>
                <select
                  className="w-full sm:w-56 p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500"
                  value={localParams.prefectureType}
                  onChange={(e) => handleChange('prefectureType', e.target.value)}
                >
                  {PrefectureTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 再出力ボタン */}
              <div className="flex gap-1 ml-auto self-end">
                <button 
                  disabled={isDisabled}
                  onClick={handleCoreSubmit}
                  className={`w-56 py-2 text-white rounded shadow-sm font-bold transition-all
                    ${isDisabled
                      ? 'bg-slate-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95 cursor-pointer'
                    }`}
                >
                  {isExporting && exportTarget === CSVOutputConst.CORE_SYSTEM ? '出力中...' : '基幹システム用CSV再出力'}
                </button>
                <button 
                  disabled={isDisabled}
                  onClick={handleSinageSubmit}
                  className={`w-56 py-2 text-white rounded shadow-sm font-bold transition-all
                    ${isDisabled
                      ? 'bg-slate-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95 cursor-pointer'
                    }`}
                >
                  {isExporting && exportTarget === CSVOutputConst.SINAGE ? '出力中...' : 'サイネージ用CSV再出力'}
                </button>
              </div>
            </div>
            {/* メッセージ */}
            <div className="mt-3 flex flex-col gap-1">
                {warningMessage && (
                  <span className="text-m text-red-500 font-medium whitespace-nowrap">
                    {warningMessage}
                  </span>
                )}
              </div>
          </div>
        </main>
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-50 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6`} />
      </button>
    </div>
  );
}